import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

const ENABLE_GRID_VIEW_TRANSITION = false;

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.#restoreGridLayout();
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);

    const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
    const inputName = viewport === 'mobile' ? 'grid-mobile' : 'grid';
    this.#syncGridDensityControls(inputName, target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    if (ENABLE_GRID_VIEW_TRANSITION) {
      await startViewTransition(() => this.#setLayout(value), ['product-grid']);
    } else {
      this.#setLayout(value);
    }

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  #restoreGridLayout() {
    const { grid } = this.refs;
    if (!grid) return;

    const storedLayoutMobile = sessionStorage.getItem('product-grid-view-mobile') || 'default';
    const storedLayoutDesktop = sessionStorage.getItem('product-grid-view-desktop') || 'default';
    const storedLayout = mediaQueryLarge.matches ? storedLayoutDesktop : storedLayoutMobile;

    grid.setAttribute('product-grid-view', storedLayout);
    this.#syncGridDensityControls('grid', storedLayoutDesktop);
    this.#syncGridDensityControls('grid-mobile', storedLayoutMobile);
  }

  /**
   * @param {string} inputName
   * @param {string} value
   */
  #syncGridDensityControls(inputName, value) {
    for (const option of this.querySelectorAll(`input[type="radio"][name="${inputName}"]`)) {
      option.checked = option.value === value;
    }
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
